import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

const defaultSettings = {
    paperSize: "A4",
    margin: {
        top: 50,
        bottom: 50,
        left: 100,
        right: 100
    },
    fontSize: {
        title: 60,
        fingerings: 40,
        lyrics: 45
    },
    spacing: {
        songBottom: 35,
        fingeringsBottom: 20,
        songTop: 150
    },
    rollBarWidth: 70,
    beatNum: 6,
    stringSpacing: 43,
    noteNum: 20,

    // 补充字体相关设置
    titleFont: 'Default',
    fingeringsFont: 'Default',
    titleColor: '#000000',
    fingeringsColor: '#000000',
    titleBold: true,
    fingeringsBold: false,
    titleItalic: false,
    fingeringsItalic: false,
    piaonHuiBold: true,
    piaonHuiItalic: false,
    lineHuiBold: false,
    lineHuiItalic: false,

    // 补充琴卷设置
    rollBarColor1: '#ffffff',
    rollBarColor2: '#EBF5FF',
    noteStrokeColor: '#007bff',
    noteFillColor: '#ffffff',
    triangleStrokeColor: '#D94D4D',
    triangleFillColor: '#FFE5B4',
    positionColor: '#000000',
    positionFont: 'Default',
    positionSize: 'medium',
    noteCornerStyle: 'square',

    // 补充七线设置
    lineColor: '#007bff',
    lineBoldColor: '#409EFF',
    lineTriangleStrokeColor: '#D94D4D',
    lineTriangleFillColor: '#FFE5B4',
    linePositionColor: '#000000',
    linePositionFont: 'Default',
    linePositionSize: 'medium',
    lineHuiBold: true,

    // 补充歌词设置
    lyricsFont: 'Default',
    lyricsColor: '#000000',
    lyricsBold: false,
    lyricsItalic: false,
    lyricsAlign: 'center',

    // 补充指法设置
    fingersFont: 'Default',
    fingersColor: '#2F4F4F',
    fingersBold: false,
    fingersItalic: false,

    // 补充作者设置
    // authorFont: 'Default',
    // authorColor: '#2F4F4F',
    // authorBold: false,
    // authorItalic: false,
}
export default new Vuex.Store({
    state: {
        settings: JSON.parse(JSON.stringify(defaultSettings)),
        svgList: [],
        noteList: [],
        lineList: [],
        currentMode: "piano-roll",
        barCount: -1,
        manualLayout: false,    // 手动排版开关
        showFingerings: false,  // 指法标注开关
        layoutMode: 'split',    // 可能的值: 'split', 'tablature', 'sidebar'
        tuningNotes: ["C", "D", "F", "G", "A", "c", "d"], // 默认 F 调
    },
    mutations: {
        updateSettings(state, payload) {
            state.settings = { ...state.settings, ...payload };
        },
        addOrUpdateSvgElement(state, element) {
            const existingIndex = state.svgList.findIndex((item) => item.id === element.id);
            if (existingIndex !== -1) {
                // 更新已有元素
                Vue.set(state.svgList, existingIndex, element);
            } else {
                // 添加新元素
                state.svgList.push(element);
            }
        },
        removeSvgElement(state, id) {
            state.svgList = state.svgList.filter((item) => item.id !== id);
        },
        clearSvgList(state) {
            state.svgList = [];
        },
        setMode(state, mode) {
            state.currentMode = mode;
        },
        updateBarCount(state, count) {
            state.barCount = count;
        },
        setManualLayout(state, value) {
            state.manualLayout = value;
        },
        setShowFingerings(state, value) {
            state.showFingerings = value;
        },
        setLayoutMode(state, mode) {
            state.layoutMode = mode;
        },
        updateTuningNotes(state, notes) {
            state.tuningNotes = notes;
        },
        updateSettings(state, payload) {
            state.settings = {
                ...state.settings,
                ...payload,
                margin: {
                    ...state.settings.margin,
                    ...(payload.margin || {})
                },
                fontSize: {
                    ...state.settings.fontSize,
                    ...(payload.fontSize || {})
                },
                spacing: {
                    ...state.settings.spacing,
                    ...(payload.spacing || {})
                }
            };
        },
        resetSettings(state) {
            // 使用深拷贝确保完全重置
            state.settings = JSON.parse(JSON.stringify(defaultSettings));
        }
    },
    actions: {
        setSettings({ commit }, newSettings) {
            commit('updateSettings', newSettings);
        },
        upsertSvgElement({ commit }, element) {
            commit("addOrUpdateSvgElement", element); 
        },
        deleteSvgElement({ commit }, id) {
            commit("removeSvgElement", id); 
        },
        resetSvgList({ commit }) {
            commit("clearSvgList");
        },
        changeMode({ commit }, mode) {
            commit("setMode", mode);
        },
        updateBarCount({ commit }, count) {
            commit("updateBarCount", count);
        },
        setTuningNotes({ commit }, payload) {
            if (payload.type === 'standard') {
                // 使用标准定调映射
                const tuningMap = {
                    'F': ["C", "D", "F", "G", "A", "c", "d"],
                    'G': ["D", "E", "G", "A", "B", "d", "e"],
                    'A': ["E", "F#", "A", "B", "C#", "e", "f#"],
                    'C': ["G", "A", "C", "D", "E", "g", "a"],
                    'D': ["A", "B", "D", "E", "F#", "a", "b"],
                    'E': ["B", "C#", "E", "F#", "G#", "b", "c#"],
                    'B': ["F#", "G#", "B", "C#", "D#", "f#", "g#"],
                    'bB': ["C", "D", "F", "G", "bB", "c", "d"]
                };
                
                const notes = tuningMap[payload.key] || tuningMap['F']; // 默认 F 调
                commit('updateTuningNotes', notes);
            } else if (payload.type === 'custom') {
                // 直接使用提供的音符
                commit('updateTuningNotes', payload.notes);
            }
        },
        resetToDefault({ commit }) {
            commit('resetSettings');
        }
    },
    getters: {
        settings: (state) => state.settings,
        svgList: (state) => state.svgList,
        noteList: (state) => state.noteList,
        lineList: (state) => state.lineList,
        currentMode: (state) => state.currentMode,
        barCount: (state) => state.barCount,
        manualLayout: state => state.manualLayout,
        showFingerings: state => state.showFingerings,
        tuningNotes: state => state.tuningNotes
    }
});
